import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    // Identifies this app to the backend so MFA is enforced for admin-app
    // logins only (the regular frontend never sends this header).
    'x-client-app': 'bozarth-admin'
  }
});

// Send the browser's IANA timezone with every request so rate-limit windows
// reset at the user's local midnight (e.g., "America/New_York" or "Asia/Kolkata").
// The backend uses this for per-user / per-IP limiters; global limiters keep
// the server-side default.
const clientTimezone = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; }
  catch { return null; }
})();

if (clientTimezone) {
  api.interceptors.request.use((config) => {
    config.headers = config.headers || {};
    if (!config.headers['X-Client-Timezone']) {
      config.headers['X-Client-Timezone'] = clientTimezone;
    }
    return config;
  });
}

// ─── Token refresh logic ───
// The backend issues an httpOnly accessToken cookie (24h) and refreshToken
// cookie (7d). On 401/403 we silently call /refresh so the user keeps their
// session until the refresh token itself expires.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

const attemptTokenRefresh = async () => {
  // Raw axios (no interceptors) to avoid recursion.
  const response = await axios.post(`${BASE_URL}/refresh`, null, {
    withCredentials: true
  });
  if (!response.data?.success) throw new Error('Refresh failed');
  return true;
};

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config;
    const status = err.response?.status;
    const url = originalRequest?.url || '';

    // Never try to refresh for the login or refresh endpoints themselves.
    const isAuthEndpoint = url.includes('/login') || url.includes('/refresh');

    if ((status === 401 || status === 403) && !isAuthEndpoint && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((e) => Promise.reject(e));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await attemptTokenRefresh();
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:expired', {
            detail: { message: 'Your session has expired. Please sign in again.' }
          }));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

// --- Auth ---
export const login = async (username, password) => {
  const { data } = await api.post('/login', { username, password });
  return data;
};

export const verifySession = async () => {
  const { data } = await api.get('/verify');
  return data;
};

export const logout = async () => {
  const { data } = await api.post('/logout');
  return data;
};

// --- MFA (TOTP / Google Authenticator) ---

// Second step of login: exchange the short-lived mfaToken + code for a real
// session. On success the backend sets the auth cookies. `username` is sent so
// the server can rate-limit verification attempts per account (keyBy username).
export const verifyMfaLogin = async (mfaToken, code, username) => {
  const { data } = await api.post('/mfa/login/verify', { mfaToken, code, username });
  return data;
};

export const getMfaStatus = async () => {
  const { data } = await api.get('/mfa/status');
  return data;
};

// Begin enrollment — returns { qrDataUrl, manualEntryKey, otpauth }.
export const setupMfa = async () => {
  const { data } = await api.post('/mfa/setup', {});
  return data;
};

// Confirm enrollment with a code — returns { backupCodes }.
export const enableMfa = async (code) => {
  const { data } = await api.post('/mfa/enable', { code });
  return data;
};

export const disableMfa = async () => {
  const { data } = await api.post('/mfa/disable', {});
  return data;
};

export const regenerateBackupCodes = async (code) => {
  const { data } = await api.post('/mfa/backup-codes/regenerate', { code });
  return data;
};

// --- Blocked numbers ---
export const fetchBlockedNumbers = async ({ page = 1, limit = 25, search = '', status = 'blocked' } = {}) => {
  const params = new URLSearchParams({ page, limit, status });
  if (search) params.append('search', search);
  const { data } = await api.get(`/blocked-numbers/blocked-numbers?${params.toString()}`);
  return data;
};

export const unblockNumber = async (username) => {
  const { data } = await api.post('/blocked-numbers/blocked-numbers/unblock', { username });
  return data;
};

// Admin-only: add a document by number with a 'blocked' or 'locked' status.
// `lockMinutes` applies only when status === 'locked' (defaults server-side).
export const addBlockedNumber = async ({ username, status, lockMinutes } = {}) => {
  const { data } = await api.post('/blocked-numbers/blocked-numbers', { username, status, lockMinutes });
  return data;
};

// Admin-only: delete a document from the collection by number.
export const deleteBlockedNumber = async (username) => {
  const { data } = await api.delete(`/blocked-numbers/blocked-numbers/${encodeURIComponent(username)}`);
  return data;
};

// --- Ext Event Subscriptions ---
export const fetchExtSubscriptions = async ({ page = 1, limit = 25, search = '', status = 'all' } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (status === 'active' || status === 'inactive') params.append('status', status);
  if (search) params.append('search', search);
  const { data } = await api.get(`/ext-subscription?${params.toString()}`);
  return data;
};

export const createExtSubscription = async (payload) => {
  const { data } = await api.post('/ext-subscription', payload);
  return data;
};

export const updateExtSubscription = async (ext, payload) => {
  const { data } = await api.patch(`/ext-subscription/${encodeURIComponent(ext)}`, payload);
  return data;
};

export const deleteExtSubscription = async (ext) => {
  const { data } = await api.delete(`/ext-subscription/${encodeURIComponent(ext)}`);
  return data;
};



// --- Rate Limit Debug (Admin) ---
export const fetchRateLimitDebugStats = async () => {
  const { data } = await api.get('/rate-limit-debug/stats');
  return data;
};

export const fetchRateLimitDebugKeys = async () => {
  const { data } = await api.get('/rate-limit-debug/keys');
  return data;
};

export const resetRateLimitKey = async (limiterName, keyValue) => {
  const { data } = await api.delete(
    `/rate-limit-debug/reset/${encodeURIComponent(limiterName)}/${encodeURIComponent(keyValue)}`
  );
  return data;
};

export const fetchRateLimitDashboard = async () => {
  const { data } = await api.get('/rate-limit-debug/dashboard');
  return data;
};

export const updateRateLimitConfig = async (limiterName, maxRequests) => {
  const { data } = await api.patch(
    `/rate-limit-debug/config/${encodeURIComponent(limiterName)}`,
    { maxRequests }
  );
  return data;
};

// --- Payment Admin ---
// Backend mounted at /admin/payment-admin (BASE_URL already includes /admin).
// `type` filter accepts: 'repair_order' | 'parts_sale'.
// `paymentStatus` (payment_links only) accepts: 'completed'.
export const fetchPaymentLinks = async ({ type = '', paymentStatus = '', limit = 50, skip = 0 } = {}) => {
  const params = new URLSearchParams({ limit, skip });
  if (type) params.append('type', type);
  if (paymentStatus) params.append('paymentStatus', paymentStatus);
  const { data } = await api.get(`/payment-admin/payment-links?${params.toString()}`);
  
  return data;
};

export const fetchPaymentStaging = async ({ type = '', limit = 50, skip = 0 } = {}) => {
  const params = new URLSearchParams({ limit, skip });
  if (type) params.append('type', type);
  const { data } = await api.get(`/payment-admin/payment-staging?${params.toString()}`);
  return data;
};

export const clearPaymentLinks = async (type = '') => {
  const qs = type ? `?type=${encodeURIComponent(type)}` : '';
  const { data } = await api.delete(`/payment-admin/payment-links${qs}`);
  return data;
};

export const clearPaymentStaging = async (type = '') => {
  const qs = type ? `?type=${encodeURIComponent(type)}` : '';
  const { data } = await api.delete(`/payment-admin/payment-staging${qs}`);
  return data;
};

// Clears BOTH collections entirely — no type filter applies here.
export const clearAllPayments = async () => {
  const { data } = await api.delete(`/payment-admin/all`);
  return data;
};

// --- Session Admin ---
// Backend mounted at /admin/sessions (BASE_URL already includes /admin).

// Force-logout ONE user: kills all their DB sessions + drops their WebSocket.
export const revokeUserSessions = async (username) => {
  const { data } = await api.post('/sessions/revoke', { username });
  return data;
};

// Force-logout EVERY user; returns a per-user breakdown of how many were revoked.
export const revokeAllSessions = async () => {
  const { data } = await api.post('/sessions/revoke-all', {});
  return data;
};

// All logged-in users with their session counts (reads DB sessions).
export const fetchSessionList = async () => {
  const { data } = await api.get('/sessions/list');
  return data;
};

// One user's sessions with device / platform / browser detail.
export const fetchUserSessions = async (username) => {
  const { data } = await api.get(`/sessions/user/${encodeURIComponent(username)}`);
  return data;
};

// Who is CONNECTED right now (live WebSocket presence).
export const fetchActiveSessions = async () => {
  const { data } = await api.get('/sessions/active');
  console.log(data,'active');
  
  return data;
};

export default api;
